/**
 * WishlistManager — Central wishlist state manager
 * Communicates with the Supabase-backed API for persistent, cross-device wishlists.
 * Fires 'wishlistUpdate' custom events so all UI components stay in sync.
 */
class WishlistManager {
  constructor() {
    this.items = [];
    this.customerId = null;
    this.isLoaded = false;
    this._loading = null;
    this.apiBase = window.__BOOKBUGS_CONFIG?.API_BASE_URL || 'https://order-return-testing-cjlu.onrender.com/api';
  }

  /**
   * Initialize the manager for a specific customer.
   * Call this once the customer ID is known (from Liquid or DOM).
   */
  async init(customerId) {
    if (!customerId || customerId === 'guest') {
      this.customerId = null;
      this.items = [];
      this.isLoaded = true;
      return;
    }

    this.customerId = String(customerId);
    await this.load();
  }

  /**
   * Fetch the full wishlist from the backend.
   * Returns a promise that resolves when loading is complete.
   */
  async load() {
    if (!this.customerId) return;

    // Deduplicate concurrent load calls
    if (this._loading) return this._loading;

    this._loading = (async () => {
      try {
        const resp = await fetch(`${this.apiBase}/wishlist?customer_id=${this.customerId}`);
        if (!resp.ok) throw new Error('Failed to fetch wishlist');
        const data = await resp.json();
        this.items = data.items || [];
      } catch (e) {
        console.error('WishlistManager: load error', e);
        this.items = [];
      } finally {
        this.isLoaded = true;
        this._loading = null;
      }
    })();

    return this._loading;
  }

  /**
   * Wait until the initial load is complete.
   */
  async waitForLoad() {
    if (this.isLoaded) return;
    if (this._loading) return this._loading;
    // Not initialized yet — wait a bit
    await new Promise(r => setTimeout(r, 300));
    if (this._loading) return this._loading;
  }

  /**
   * Add a book to the wishlist.
   * @param {Object} bookData - { handle, productId, variantId, title, imageUrl, author }
   */
  async add(bookData) {
    if (!this.customerId) return false;

    try {
      const resp = await fetch(`${this.apiBase}/wishlist/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: this.customerId,
          handle: bookData.handle,
          product_id: String(bookData.productId),
          variant_id: String(bookData.variantId),
          title: bookData.title,
          image_url: bookData.imageUrl || null,
          author: bookData.author || null
        })
      });

      if (!resp.ok) throw new Error('Failed to add to wishlist');

      const data = await resp.json();
      if (data.success && data.item) {
        // Update local cache — replace if exists, else push
        const idx = this.items.findIndex(i => i.handle === bookData.handle);
        if (idx >= 0) {
          this.items[idx] = data.item;
        } else {
          this.items.unshift(data.item);
        }
        this._fireUpdate();
        return true;
      }
      return false;
    } catch (e) {
      console.error('WishlistManager: add error', e);
      return false;
    }
  }

  /**
   * Remove a book from the wishlist by handle.
   * @param {string} handle - Product handle
   */
  async remove(handle) {
    if (!this.customerId) return false;

    try {
      const resp = await fetch(`${this.apiBase}/wishlist/remove`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: this.customerId,
          handle: handle
        })
      });

      if (!resp.ok) throw new Error('Failed to remove from wishlist');

      // Update local cache
      this.items = this.items.filter(i => i.handle !== handle);
      this._fireUpdate();
      return true;
    } catch (e) {
      console.error('WishlistManager: remove error', e);
      return false;
    }
  }

  /**
   * Check if a book is currently in the wishlist.
   * @param {string} handle - Product handle
   * @returns {boolean}
   */
  isWishlisted(handle) {
    return this.items.some(i => i.handle === handle);
  }

  /**
   * Get all wishlist items.
   * @returns {Array}
   */
  getAll() {
    return this.items;
  }

  /**
   * Get the wishlist count.
   * @returns {number}
   */
  getCount() {
    return this.items.length;
  }

  /**
   * Fire a custom event to notify all UI components of a wishlist change.
   */
  _fireUpdate() {
    document.dispatchEvent(new CustomEvent('wishlistUpdate', {
      detail: { count: this.items.length, items: this.items }
    }));
  }
}

// Create and expose singleton
window.wishlistManager = new WishlistManager();
